<template>
  <div id="striiv-pulse-line-graph" :class="{'noData' : !hasData && !isLoading }">
    <div class="loadingStatus">
      <mu-circular-progress class="noDataStatus" v-if="isLoading" :size="60" align="center"
                            color="#F75039"></mu-circular-progress>
    </div>
    <line-chart width="1500" v-if="!isLoading && hasData" :data="chartData" :options="chartOptions"
                :height="computedHeight"></line-chart>
    <div class="noDataStatus" v-if="!hasData && !isLoading">
      <alert type="info" icon="info" align="top">
        <div slot="content">
          Not enough data to create a chart
        </div>
      </alert>
    </div>
  </div>
</template>

<script>

  import LineChart from './LineGraph.js';
  import graphApi from '../../../../api/Graphs';
  import Alert from "../../../Alert/Alert.vue";


  export default {
    watch: {
      "date": function () {
        let self = this;
        self.data = [];
        self.chartData = null;
        self.isLoading = true;
        if (graphApi.Cancel !== null) {
          graphApi.Cancel();
        }
        self.getPulseReadings();
      }
    },
    components: {
      Alert,
      LineChart
    },
    name: 'striiv-pulse-line-graph',
    data() {
      return {
        isLoading: true,
        chartData: null,
        chartOptions: null,
        data: []
      }
    },
    props: {
      date: {
        type: String,
        required: true,
      }
    },
    methods: {
      getPulseReadings() {
        let self = this;
        graphApi.getHeartRateGraph({
          limit: 500,
          skip: self.data.length,
          service: 'STRIIV',
          date: self.date
        }).then(function (response) {
          let res = response.data;
          if (res.data.length === 0) {
            self.isLoading = false;
            let heartStats = {
              date: moment(self.date),
              min: res.stats.min,
              max: res.stats.max,
              avg: res.stats.avg,
            };
            self.$EventBus.$emit('emitHeartRateStats', heartStats);
            self.renderGraph(self.data);
          } else {
            self.data.push.apply(self.data, res.data);
            self.getPulseReadings();
          }
        });
        if (self.hasData) {
          self.renderGraph([]);
        }
      },
      renderGraph(data) {
        let self = this;
        let datasetData = [];
        let datasetLabels = [];

        if (data.length) {
          data.forEach(function (item) {
            if (item.bpm !== 0) {
              datasetData.push(item.bpm);
              let date = moment(item.date).utc().local();
              if (date.isDST()) {
                date.add(1, 'hour');
              }
              datasetLabels.push(date.format());
            }
          });

          let datasetTemplate = {
            borderCapStyle: 'butt',
            borderColor: '#FA6956',
            fill: true,
            lineTension: 0.1,
            pointBackgroundColor: '#FFFFFF',
            pointBorderColor: "#FA6956",
            pointBorderWidth: 0,
            pointHitRadius: 10,
            pointHoverBackgroundColor: "#FFF",
            pointHoverBorderColor: "#FFF",
            pointHoverBorderWidth: 1,
            pointHoverRadius: 2,
            pointRadius: 0,
            data: datasetData
          };

          self.chartData = {
            labels: datasetLabels,
            datasets: [datasetTemplate]
          };

          self.chartOptions = {
            responsive: false,
            maintainAspectRatio: true,
            tooltips: {
              cornerRadius: 5,
              mode: "nearest",
              enabled: true,
              displayColors: false,
              xPadding: 16,
              yPadding: 16,
              titleFontFamily: "Poppins",
              titleFontStyle: "200",
              bodyFontFamily: "Poppins",
              backgroundColor: "#0179d5",
              titleFontSize: 16,
              titleFontColor: "white",
              bodyFontColor: "white",
              bodyFontSize: 14,

              callbacks: {
                title: function (tooltipItem) {
                  return moment(tooltipItem[0].xLabel).format("DD-MM-YYYY HH:mm a");
                },
                label: function (tooltipItem, data) {
                  let label = data.datasets[tooltipItem.datasetIndex].label;
                  let value = tooltipItem.yLabel;
                  return tooltipItem.yLabel + ' ' + 'BPM'
                }
              }
            },
            animation: {
              duration: 0,
            },
            legend: {
              display: false,
            },
            scales: {
              xAxes: [{
                type: 'time',
                time: {
                  unit: 'hour'
                },
                gridLines: {
                  display: false,
                  showLine: false,
                  lineWidth: 0,
                }
              }],
              yAxes: [{
                time: {
                  unit: 'hour'
                },
                gridLines: {
                  spanGaps: false,
                  showLine: false,
                  display: true,
                  lineWidth: 1,
                  borderWidth: 0,
                  borderDashOffset: 0,
                  borderDash: [6, 8],
                },
                ticks: {
                  padding: 0,
                  fontFamily: 'Poppins',
                  fontSize: 13,
                  beginAtZero: false
                }
              }]
            }
          };
        }
      },
      getScrollbarHeight() {
        let outer = document.createElement("div");
        outer.style.visibility = "hidden";
        outer.style.width = "100px";
        outer.style.msOverflowStyle = "scrollbar"; // needed for WinJS apps
        document.body.appendChild(outer);
        let widthNoScroll = outer.offsetWidth;
        outer.style.overflow = "scroll";
        let inner = document.createElement("div");
        inner.style.width = "100%";
        outer.appendChild(inner);
        let widthWithScroll = inner.offsetWidth;
        outer.parentNode.removeChild(outer);
        return widthNoScroll - widthWithScroll;
      },
      initMounted() {
        let self = this;
        self.getPulseReadings();
      }
    },
    mounted() {
      let self = this;
      self.initMounted();
    },
    computed: {
      hasData() {
        let self = this;
        return self.data.length !== 0;
      },
      computedHeight() {
        let self = this;
        return 340 - self.getScrollbarHeight();
      }
    }
  }

</script>
<style lang="less">
  #striiv-pulse-line-graph {
    min-height: 341px;
    text-align: center;
    position: relative;
    overflow-x: scroll;
    &.noData {
      &:before {
        width: 100%;
        height: 100%;
        content: '';
        background: fade(#FFF, 75%);
        position: absolute;
        top: 0;
        left: 0;
      }
    }
    .mu-circular-progress {
      margin: 20px 0;
    }
    .noDataStatus, .loadingStatus {
      max-width: 300px;
      width: 100%;
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 9;
      -webkit-transform: translate(-50%, -50%);
      -moz-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
    }
  }
</style>



// WEBPACK FOOTER //
// LineGraph.vue?f58be59c